import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { faCheck, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const OPTION_ROW_HEIGHT = 30;
const OPTION_LIST_HEIGHT = 500;
const OPTION_OVERSCAN = 8;

const ComboWrapper = styled.div`
  position: relative;
  max-width: 300px;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid var(--app-border);
  font-size: 16px;
  padding: 11px 38px 11px 12px;

  box-sizing: border-box;
  border-radius: 8px;
  margin-top: 0px;
  background-color: #fff;
  color: var(--app-ink);
  outline: none;
  max-width: 300px;
  transition: border-color 140ms ease-in, box-shadow 140ms ease-in;

  &:focus {
    border-color: #09bfa5;
    box-shadow: 0 0 0 3px rgba(9, 191, 165, 0.16);
  }
`;

const ComboOptionsElement = styled.div`
  background-color: #fff;
  z-index: 100000;
  position: absolute;
  top: 46px;
  left: 0;
  max-height: ${OPTION_LIST_HEIGHT}px;
  height: ${(props) =>
    props.itemCount
      ? `${Math.min(props.itemCount * OPTION_ROW_HEIGHT, OPTION_LIST_HEIGHT)}px`
      : "auto"};
  width: 400px;
  overflow: auto;
  box-shadow: var(--app-shadow);
  border: 1px solid var(--app-border);
  border-radius: 8px;
  padding-left: 10px;
  padding-top: 2px;
`;

const ComboOptionsInner = styled.div`
  position: relative;
  height: ${(props) => props.height}px;
`;

const ComboOptionsSlice = styled.div`
  position: absolute;
  top: ${(props) => props.offset}px;
  left: 0;
  right: 0;
`;

const ComboOptionElement = styled.div`
  align-items: center;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  height: 100%;
  padding: 0 4px;
  position: relative;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  &:hover {
    background-color: var(--app-surface-soft);
    color: var(--app-ink);
  }

  .selection-marker {
    display: inline-block;
    position: absolute;
    left: -14px;
    top: 0;
  }

  &.hover .selection-marker {
    color: #000;
  }
  font-size: 16px;

  &.match {
    background-color: #e8fbf7;
  }
`;

const OptionLevel = styled.div`
  ${(props) =>
    `padding-left: ${
      5 * (props.level + 1) + 10 * ((props.level * (props.level + 1)) / 2)
    }px;`}
  height: ${OPTION_ROW_HEIGHT}px;
  box-sizing: border-box;
`;

const SelectedIcon = styled(FontAwesomeIcon)`
  color: #000;
  font-size: 12px;
  line-height: 18px;
  margin-right: 5px;
`;

const DownArrow = styled(FontAwesomeIcon)`
  position: absolute;
  color: #000;
  top: 12px;
  right: 12px;
`;

const NoResults = styled.div`
  padding: 10px 0;
`;

function useOutsideAlerter(refs, fn) {
  useEffect(() => {
    function handleClickOutside(event) {
      const mapped = refs.map((ref) => {
        if (ref.current && !ref.current.contains(event.target)) {
          return true;
        }
        return false;
      });

      let isOutside = !mapped.includes(false);

      if (isOutside) {
        fn && fn();
      }
    }
    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [refs, fn]);
}

const findInTree = (value, options) => {
  let match;

  (options || []).forEach((option) => {
    if (!option) {
      return;
    }

    if (Number(option.value) === Number(value)) {
      match = option;
    } else if (option.children) {
      const childMatch = findInTree(value, option.children);
      if (childMatch) {
        match = childMatch;
      }
    }
  });

  return match;
};

const filterRecursive = (options, search) => {
  const searchTerm = (search || "").toLowerCase().trim();

  if (!searchTerm) {
    return options || [];
  }

  const recurse = (level) => {
    return (level || [])
      .map((item) => ({ ...item }))
      .filter((item) => {
        if (item.name && item.name.toLowerCase().includes(searchTerm)) {
          return true;
        } else if (item.children && item.children.length) {
          const childResult = recurse(item.children);

          item.children = childResult;

          return childResult.length;
        }
        return false;
      });
  };

  return recurse(options);
};

const flattenOptions = (options = []) => {
  const flattened = [];

  const recurse = (level = [], depth = 0) => {
    (level || []).forEach((option) => {
      if (!option) {
        return;
      }

      const hasDisplayName = option.name && option.name.trim();

      if (hasDisplayName) {
        flattened.push({ option, depth });
      }

      if (option.children && option.children.length) {
        recurse(option.children, hasDisplayName ? depth + 1 : depth);
      }
    });
  };

  recurse(options);

  return flattened;
};

const escapeRegExp = (value) => {
  return (value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const getHighlightedText = (text, highlight) => {
  const safeHighlight = highlight ? escapeRegExp(highlight.trim()) : "";
  const parts =
    text && safeHighlight
      ? text.split(new RegExp(`(${safeHighlight})`, "gi"))
      : [text];

  return (
    <span>
      {" "}
      {parts.map((part, i) => (
        <span
          key={i}
          style={
            safeHighlight &&
            part &&
            part.toLowerCase() === highlight.trim().toLowerCase()
              ? { fontWeight: "bold" }
              : {}
          }
        >
          {part}
        </span>
      ))}{" "}
    </span>
  );
};

const ComboOption = React.forwardRef(
  ({ depth, isSelected, onClick, option, searchVal }, ref) => {
    return (
      <OptionLevel level={depth}>
        <ComboOptionElement ref={ref} onClick={() => onClick(option.value)}>
          {isSelected && (
            <div className="selection-marker">
              <SelectedIcon icon={faCheck} />
            </div>
          )}
          {getHighlightedText(option.name, searchVal)}
        </ComboOptionElement>
      </OptionLevel>
    );
  }
);

const ComboOptions = React.forwardRef(
  ({ options, value, onClick, searchVal }, ref) => {
    const containerRef = useRef(null);
    const selectedRef = useRef(null);
    const [scrollTop, setScrollTop] = useState(0);
    const flattenedOptions = useMemo(() => flattenOptions(options), [options]);
    const selectedIndex = useMemo(
      () =>
        flattenedOptions.findIndex(
          ({ option }) => Number(value) === Number(option.value)
        ),
      [flattenedOptions, value]
    );
    const startIndex = Math.max(
      Math.floor(scrollTop / OPTION_ROW_HEIGHT) - OPTION_OVERSCAN,
      0
    );
    const visibleCount = Math.ceil(OPTION_LIST_HEIGHT / OPTION_ROW_HEIGHT);
    const endIndex = Math.min(
      startIndex + visibleCount + OPTION_OVERSCAN * 2,
      flattenedOptions.length
    );
    const visibleOptions = flattenedOptions.slice(startIndex, endIndex);

    const setContainerRef = (node) => {
      containerRef.current = node;

      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    useEffect(() => {
      if (!containerRef.current) {
        return;
      }

      if (searchVal) {
        containerRef.current.scrollTop = 0;
        setScrollTop(0);
        return;
      }

      if (selectedIndex > -1) {
        const targetTop = selectedIndex * OPTION_ROW_HEIGHT;
        const centeredTop = Math.max(
          targetTop - OPTION_LIST_HEIGHT / 2 + OPTION_ROW_HEIGHT / 2,
          0
        );

        containerRef.current.scrollTop = centeredTop;
        setScrollTop(centeredTop);
      }
    }, [searchVal, selectedIndex]);

    return (
      <ComboOptionsElement
        ref={setContainerRef}
        itemCount={flattenedOptions.length}
        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
      >
        {flattenedOptions.length ? (
          <ComboOptionsInner
            height={flattenedOptions.length * OPTION_ROW_HEIGHT}
          >
            <ComboOptionsSlice offset={startIndex * OPTION_ROW_HEIGHT}>
              {visibleOptions.map(({ option, depth }, offset) => {
                const isSelected = Number(value) === Number(option.value);

                return (
                  <ComboOption
                    key={`${option.value}-${startIndex + offset}`}
                    ref={isSelected ? selectedRef : null}
                    depth={depth}
                    onClick={() => onClick(option.value)}
                    isSelected={isSelected}
                    option={option}
                    searchVal={searchVal}
                  />
                );
              })}
            </ComboOptionsSlice>
          </ComboOptionsInner>
        ) : (
          <NoResults>No results found</NoResults>
        )}
      </ComboOptionsElement>
    );
  }
);

function Combo({ options, onChange, className, value }) {
  const [selected, setSelected] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [search, setSearch] = useState("");
  const [placeholder, setPlaceholder] = useState([]);

  const inputRef = useRef(null);

  const onClickFn = (newValue) => {
    setShowOptions(false);
    setSearch("");
    onChange(newValue);
  };

  const comboRef = useRef(null);
  const closeOptions = useCallback(() => {
    setShowOptions(false);
    setSearch("");
  }, []);
  const outsideRefs = useMemo(() => [inputRef, comboRef], []);
  useOutsideAlerter(outsideRefs, closeOptions);

  useEffect(() => {
    if (options && value) {
      setSelected(findInTree(value, options));
    }
  }, [value, options]);

  const activeOptions = useMemo(
    () => filterRecursive(options || [], search),
    [options, search]
  );

  useEffect(() => {
    if (showOptions) {
      setPlaceholder("Search");
    } else {
      setPlaceholder(selected && selected.name);
    }
  }, [showOptions, selected]);

  return (
    <ComboWrapper className={className}>
      <DownArrow
        icon={faChevronDown}
        onClick={(e) => {
          setShowOptions(true);
        }}
      />
      <Input
        placeholder={placeholder}
        ref={inputRef}
        onClick={(e) => {
          setShowOptions(true);
          e.target.select();
        }}
        value={showOptions ? search : ""}
        onChange={(e) => setSearch(e.target.value)}
      ></Input>
      {showOptions && (
        <ComboOptions
          options={activeOptions}
          ref={comboRef}
          onClick={onClickFn}
          value={value}
          searchVal={search}
        />
      )}
    </ComboWrapper>
  );
}

export default Combo;
